import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="container py-5">

      <h1 className="fw-bold mb-4">Privacy Policy</h1>

      <p className="text-muted" style={{ fontSize: "16px" }}>
        This Privacy Policy describes how <strong>SWIFTSHIP LLC</strong> (“Company”, “we”, “our”) 
        collects, uses, protects, and processes your personal information while ensuring full 
        compliance with U.S. privacy regulations, including <strong>FTC guidelines, CCPA, COPPA, 
        GDPR (where applicable), and PCI-DSS standards</strong>.
        <br /><br />
        By accessing our website or using our logistics, shipping, warehousing, or fulfillment 
        services, you agree to the practices outlined below.
      </p>

      <hr className="my-4" />

      {/* SECTION 1 */}
      <h3 className="fw-bold">1. Information We Collect</h3>
      <p className="text-muted">
        We collect only the information required to operate our logistics platform, prevent fraud, 
        fulfill shipments, and comply with U.S. transportation & customs regulations. This includes:
      </p>

      <ul className="text-muted">
        <li>✔ Personal identification (full name, email, phone number)</li>
        <li>✔ Pickup, delivery & warehouse addresses</li>
        <li>✔ Package details (dimensions, weight, declared value, contents)</li>
        <li>✔ Business information (company name, EIN, billing contact)</li>
        <li>✔ Browser/device data, IP address, geolocation & security identifiers</li>
        <li>✔ Communication logs (support tickets, emails, customer requests)</li>
        <li>✔ Payment information processed through PCI-DSS compliant partners</li>
      </ul>

      <p className="text-muted">
        <strong>We do not store complete credit/debit card numbers.</strong> All financial data is 
        securely handled by third-party processors.
      </p>

      <hr className="my-4" />

      {/* SECTION 2 */}
      <h3 className="fw-bold">2. How We Use Your Information</h3>
      <p className="text-muted">Your data is used for the following purposes:</p>

      <ul className="text-muted">
        <li>✔ Creating shipments, freight orders & service requests</li>
        <li>✔ Providing real-time tracking & delivery notifications</li>
        <li>✔ Verifying identity to prevent fraud or unauthorized use</li>
        <li>✔ Generating customs documents & government-required reports</li>
        <li>✔ Improving website functionality & customer support</li>
        <li>✔ Ensuring compliance with U.S. federal & international logistics laws</li>
      </ul>

      <p className="text-muted">
        SWIFTSHIP does <strong>not sell, rent, or trade</strong> personal information.
      </p>

      <hr className="my-4" />

      {/* SECTION 3 */}
      <h3 className="fw-bold">3. Delivery Timeframes & Operational Expectations</h3>
      <p className="text-muted">
        By using our services, customers understand that logistics timelines may require 
        <strong>30–45 days or longer</strong> depending on:
      </p>

      <ul className="text-muted">
        <li>✔ International customs inspections & legal procedures</li>
        <li>✔ Carrier capacity, routing, freight schedules & transit conditions</li>
        <li>✔ Weather delays, seasonal surges, or operational disruptions</li>
      </ul>

      <p className="text-muted">
        These industry-standard transit variations do not qualify for disputes, refunds, or 
        chargebacks unless a verified service failure has occurred.
      </p>

      <hr className="my-4" />

      {/* SECTION 4 */}
      <h3 className="fw-bold">4. Cookies, Tracking & Analytics</h3>
      <p className="text-muted">
        Our website uses cookies, analytics tools (such as Google Analytics), and anti-fraud 
        technologies to:
      </p>

      <ul className="text-muted">
        <li>✔ Improve navigation & overall website performance</li>
        <li>✔ Analyze usage trends for service enhancement</li>
        <li>✔ Detect fraudulent or suspicious activity</li>
      </ul>

      <p className="text-muted">
        Users may manage preferences through the <strong>Cookie Settings</strong> available on our website.
      </p>

      <p className="text-muted">
        For full details, please review our <strong>Cookie Policy</strong>.
      </p>

      <hr className="my-4" />

      {/* SECTION 5 */}
      <h3 className="fw-bold">5. Data Protection, Storage & Processing</h3>
      <p className="text-muted">
        We implement enterprise-grade protections, including:
      </p>

      <ul className="text-muted">
        <li>✔ SSL encrypted data transmission</li>
        <li>✔ Secure U.S.-based cloud infrastructure</li>
        <li>✔ Firewall, DDoS protection & intrusion detection</li>
        <li>✔ Staff access controls & encrypted storage</li>
        <li>✔ PCI-DSS compliance for all payment operations</li>
      </ul>

      <p className="text-muted">
        All data is processed on servers located in the <strong>United States</strong>.
      </p>

      <hr className="my-4" />

      {/* SECTION 6 */}
      <h3 className="fw-bold">6. Third-Party Information Sharing</h3>
      <p className="text-muted">
        We share data only with partners essential to delivering your shipments:
      </p>

      <ul className="text-muted">
        <li>✔ U.S. & international carriers, couriers & freight companies</li>
        <li>✔ U.S. Customs & Border Protection and government authorities</li>
        <li>✔ PCI-DSS certified payment processors & banks</li>
        <li>✔ Cloud hosting, analytics & fraud-monitoring providers</li>
      </ul>

      <p className="text-muted">
        We never share personal data with unauthorized parties or marketers without consent.
      </p>

      <hr className="my-4" />

      {/* SECTION 7 */}
      <h3 className="fw-bold">7. Chargeback Prevention & Fraud Monitoring</h3>
      <p className="text-muted">
        To maintain secure operations, SWIFTSHIP applies advanced fraud-prevention systems. 
        Customers agree to:
      </p>

      <ul className="text-muted">
        <li>✔ Contact SWIFTSHIP before initiating a chargeback or dispute</li>
        <li>✔ Provide accurate shipment details & legal documentation</li>
        <li>✔ Understand that standard transit delays are not dispute-eligible</li>
        <li>✔ Accept that fraudulent disputes may result in account suspension or legal action</li>
      </ul>

      <p className="text-muted">
        We maintain shipment logs, tracking records, signatures & delivery proofs for dispute resolution.
      </p>

      <hr className="my-4" />

      {/* SECTION 8 */}
      <h3 className="fw-bold">8. Data Retention Policy</h3>
      <p className="text-muted">
        We retain personal and shipment-related information only for as long as necessary to:
      </p>

      <ul className="text-muted">
        <li>✔ Provide active services and track deliveries</li>
        <li>✔ Maintain legal, tax, and customs compliance</li>
        <li>✔ Support dispute resolution & fraud monitoring</li>
      </ul>

      <p className="text-muted">
        After required periods, data is securely deleted or anonymized.
      </p>

      <hr className="my-4" />

      {/* SECTION 9 */}
      <h3 className="fw-bold">9. Children’s Privacy (COPPA)</h3>
      <p className="text-muted">
        Our services are not intended for individuals under the age of 13.  
        We do not knowingly collect information from children. If such data is discovered, it is deleted immediately.
      </p>

      <hr className="my-4" />

      {/* SECTION 10 */}
      <h3 className="fw-bold">10. Do-Not-Track (DNT) Disclosure</h3>
      <p className="text-muted">
        Our website currently does not respond to browser-based Do-Not-Track signals due to a lack 
        of uniform standards. However, users may disable cookies or tracking tools manually.
      </p>

      <hr className="my-4" />

      {/* SECTION 11 */}
      <h3 className="fw-bold">11. Your Rights</h3>
      <p className="text-muted">Users may request:</p>

      <ul className="text-muted">
        <li>✔ Access to personal data</li>
        <li>✔ Correction of inaccurate information</li>
        <li>✔ Deletion of personal data (as legally permitted)</li>
        <li>✔ Opt-out of marketing communications</li>
        <li>✔ Restriction on certain types of data processing</li>
      </ul>

      <p className="text-muted">
        To submit a privacy request, contact us at <strong>info@swiftship.us</strong>.
      </p>

      <hr className="my-4" />

      {/* SECTION 12 */}
      <h3 className="fw-bold">12. Updates to This Policy</h3>
      <p className="text-muted">
        This Privacy Policy may be updated periodically to reflect regulatory, security, or operational changes.
        The updated version will always be posted on this page.
      </p>

      <hr className="my-4" />

      {/* SECTION 13 */}
      <h3 className="fw-bold">13. Contact Information</h3>
      <p className="text-muted">
        For privacy concerns or compliance-related inquiries:
      </p>

      <p className="mt-2">
        <strong>Email:</strong> info@swiftship.us <br />
        <strong>Address:</strong> 3800 Cabell Dr, Irving, TX 75063, United States <br />
        <strong>Phone:</strong> (480) 520-0274
      </p>

    </div>
  );
}
